import type { supabase } from '@/integrations/supabase/client';

type ReadClient = Pick<typeof supabase, 'from'>;
export type CoachingIdentity = { leaderId: string; coachId: string };
export type LeaderSharedAgreement = {
  id: string;
  createdAt: string | null;
  updatedAt: string | null;
  discussed: string[];
  nextFocus: string[];
  nextSession: string[];
  actionItems: string[];
  reflectionPrompts: string[];
};

export const SHARED_AGREEMENT_PAGE_SIZE = 20;
// This leader reader deliberately excludes coach notes, evaluation and raw sources.
export const SHARED_AGREEMENT_SELECT = 'id, leader_id, coach_id, created_at, updated_at, worked_on, direction, next_conversation_signals, leader_plan_draft_v1, published_to_leader';

function lines(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((line): line is string => typeof line === 'string').map(line => line.trim()).filter(Boolean) : [];
}

export function normalizePublishedAgreement(row: Record<string, unknown>, identity: CoachingIdentity): LeaderSharedAgreement | null {
  if (row.leader_id !== identity.leaderId || row.coach_id !== identity.coachId || row.published_to_leader !== true || typeof row.id !== 'string') return null;
  const raw = row.leader_plan_draft_v1;
  let content: Pick<LeaderSharedAgreement, 'discussed' | 'nextFocus' | 'nextSession' | 'actionItems' | 'reflectionPrompts'>;
  if (raw != null) {
    if (typeof raw !== 'object' || Array.isArray(raw)) return null;
    const plan = raw as Record<string, unknown>;
    const fields = ['whatDiscussed', 'nextFocus', 'nextSession', 'actionItems', 'reflectionPrompts'];
    if (plan.version !== 'leader_plan_draft_v1' || plan.approvalState !== 'approved'
      || fields.some(key => !Array.isArray(plan[key]) || !(plan[key] as unknown[]).every(line => typeof line === 'string'))) return null;
    content = {discussed:lines(plan.whatDiscussed),nextFocus:lines(plan.nextFocus),nextSession:lines(plan.nextSession),actionItems:lines(plan.actionItems),reflectionPrompts:lines(plan.reflectionPrompts)};
  } else {
    content = {discussed:lines(row.worked_on),nextFocus:lines(row.direction),nextSession:lines(row.next_conversation_signals),actionItems:[],reflectionPrompts:[]};
  }
  return {id:row.id,createdAt:typeof row.created_at === 'string' ? row.created_at : null,updatedAt:typeof row.updated_at === 'string' ? row.updated_at : null,...content};
}

export async function isCurrentCoachLink(client: ReadClient, identity: CoachingIdentity & { relationshipRevision?: string }): Promise<boolean> {
  const {data,error} = await client.from('coach_leader_links').select('coach_id, relationship_revision').eq('leader_id',identity.leaderId).eq('coach_id',identity.coachId).maybeSingle();
  if (error) throw error;
  return data?.coach_id === identity.coachId && (!identity.relationshipRevision || data.relationship_revision === identity.relationshipRevision);
}

export async function loadLeaderSharedAgreementsPage({client, page, recordId, ...identity}: CoachingIdentity & {client:ReadClient;page:number;recordId?:string}) {
  const unavailable = {agreements:[] as LeaderSharedAgreement[],relationshipActive:false,nextPage:undefined as number | undefined};
  if (!await isCurrentCoachLink(client,identity)) return unavailable;
  const start = page * SHARED_AGREEMENT_PAGE_SIZE;
  let query = client.from('coach_leader_session_records').select(SHARED_AGREEMENT_SELECT)
    .eq('leader_id',identity.leaderId).eq('coach_id',identity.coachId).eq('published_to_leader',true);
  if (recordId) query = query.eq('id',recordId);
  const {data,error} = await query.order('created_at',{ascending:false}).order('id',{ascending:false}).range(start,start+SHARED_AGREEMENT_PAGE_SIZE-1);
  if (error) throw error;
  if (!await isCurrentCoachLink(client,identity)) return unavailable;
  const rows = data ?? [];
  return {agreements:rows.map(row=>normalizePublishedAgreement(row,identity)).filter((row):row is LeaderSharedAgreement=>row!==null),relationshipActive:true,nextPage:!recordId && rows.length===SHARED_AGREEMENT_PAGE_SIZE ? page+1 : undefined};
}

type SharedDocumentCandidate = {
  id: string; leaderId: string; coachId: string; senderId: string;
  attachmentBucket: string | null; attachmentPath: string | null; attachmentName: string | null;
};
export function selectCoachSharedDocuments<T extends SharedDocumentCandidate>(messages: T[], identity: CoachingIdentity): T[] {
  return messages.filter(message => message.leaderId===identity.leaderId && message.coachId===identity.coachId
    && message.senderId===identity.coachId && message.attachmentBucket==='coach_message_attachments'
    && message.attachmentPath?.startsWith(`leader/${identity.leaderId}/coach-messages/`)
    && !message.attachmentPath.split('/').some(segment=>segment==='..' || segment==='.')
    && Boolean(message.attachmentName?.trim()));
}

export function coachingRecordDate(value: string | null): string {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? new Intl.DateTimeFormat(undefined,{dateStyle:'medium'}).format(date) : 'Date unavailable';
}

/** Storage's leader namespace is broader than the current coach relationship. */
export async function downloadSharedCoachingAttachment({client, identity, message, canRelease}: {
  client:Pick<typeof supabase,'from' | 'storage'>;
  identity:CoachingIdentity;
  message:SharedDocumentCandidate;
  canRelease:()=>boolean;
}) {
  const currentParticipant = message.senderId===identity.coachId || message.senderId===identity.leaderId;
  const valid = selectCoachSharedDocuments([{...message,senderId:identity.coachId}],identity).length===1 && currentParticipant;
  if (!valid || !await isCurrentCoachLink(client,identity)) throw new Error('This document is no longer available.');
  const {data,error} = await client.storage.from(message.attachmentBucket!).download(message.attachmentPath!);
  if (error) throw error;
  if (!data) throw new Error('Document unavailable');
  if (!await isCurrentCoachLink(client,identity)) throw new Error('This document is no longer available.');
  if (!canRelease()) return;
  const objectUrl=URL.createObjectURL(data);
  const anchor=document.createElement('a');
  anchor.href=objectUrl;anchor.download=message.attachmentName || 'Document';anchor.rel='noopener noreferrer';
  document.body.appendChild(anchor);anchor.click();anchor.remove();
  window.setTimeout(()=>URL.revokeObjectURL(objectUrl),1000);
}
